/** Small presentational pieces shared by catalog tiles, question cards and the market header. */
import { useEffect, useState } from 'react';
import { CATEGORY_LABEL, type Category, type DetailRow } from './describe';
import { formatCountdown, formatTime, formatUtc } from './format';
import { useT } from '../i18n/useT';
import { whenLabel } from './when';

const CATEGORY_CLS: Record<Category, string> = {
  'crypto price': 'border-accent/40 text-accent',
  sports: 'border-fg/30 text-fg/80',
  event: 'border-warn/50 text-warn',
  other: 'border-border text-muted',
};

/**
 * Shared box of every chip in a meta row (category, Touch, #id, venue): one fixed height, content centred, so the
 * chips of the market header and the market cards sit on one line with no offset between them (#873).
 */
export const CHIP = 'inline-flex h-5 shrink-0 items-center leading-none';

/** "Venue: <name>": plain label, then the venue id (data, not translated) bold in a slightly lighter box (#873). */
export function VenueChip({ venue }: { venue: string }) {
  const t = useT();
  return (
    <span className={`${CHIP} gap-1`} data-testid="venue-chip">
      <span>{t('market.header.venue')}</span>{' '}
      <span className="inline-flex h-5 items-center rounded bg-surface-2 px-1.5 font-semibold text-fg" translate="no" data-testid="venue-name">
        {venue}
      </span>
    </span>
  );
}

export function CategoryChip({ category }: { category: Category }) {
  useT();
  return (
    <span
      className={`${CHIP} rounded-full border px-1.5 text-[10px] font-medium uppercase tracking-wide ${CATEGORY_CLS[category]}`}
      data-testid="category-chip"
    >
      {CATEGORY_LABEL[category]}
    </span>
  );
}

let listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;
function subscribeMinute(fn: () => void): () => void {
  listeners.add(fn);
  if (!timer) timer = setInterval(() => listeners.forEach((l) => l()), 30_000);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = undefined;
      listeners = new Set();
    }
  };
}

/** Re-renders about every 30 s (one shared interval for all countdowns on the page). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => subscribeMinute(() => setNow(new Date())), []);
  return now;
}

/** describe/'s resolvesLabel (English ids "Resolves" / "Starts" / "Decision" / "Deadline") → catalog keys. */
/** "Resolves in 2d 4h" (local time and UTC on hover); "Resolved" style once past. */
export function Countdown({ date, label = 'Resolves' }: { date: Date; label?: string }) {
  const t = useT();
  const now = useNow();
  const left = formatCountdown(date, now);
  const past = date.getTime() <= now.getTime();
  const verb = past
    ? label === 'Starts'
      ? t('describe.when.started')
      : label === 'Resolves'
        ? t('describe.when.resolution_due')
        : t('describe.when.passed', { label: whenLabel(label) })
    : whenLabel(label);
  return (
    <span
      className={`num text-[11px] ${past ? 'text-muted' : 'text-fg/80'}`}
      title={`${formatTime(date)} · ${formatUtc(date)}`}
      data-testid="countdown"
    >
      {t('describe.countdown.line', { label: verb, when: past ? formatTime(date) : left })}
    </span>
  );
}

/** Local time with the UTC time on hover. */
export function LocalTime({ date }: { date: Date }) {
  useT();
  return (
    <time dateTime={date.toISOString()} title={formatUtc(date)}>
      {formatTime(date)}
    </time>
  );
}

export function DetailsTable({ rows }: { rows: readonly DetailRow[] }) {
  useT();
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1 text-xs" data-testid="outcome-details">
      {rows.map((r, i) => (
        <div key={`${r.label}-${i}`} className="contents">
          <dt className="text-muted" translate={r.labelIsData ? 'no' : undefined}>
            {r.label}
          </dt>
          <dd className="min-w-0 break-words" title={r.hint} translate="no">
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Collapsed raw outcomeMeta name + description (nothing is ever hidden for good). */
export function RawDescription({ name, description, label }: { name: string; description: string; label?: string }) {
  const t = useT();
  return (
    <details className="text-xs text-muted" data-testid="raw-description">
      <summary className="cursor-pointer select-none hover:text-fg">{label ?? t('describe.raw')}</summary>
      <div className="mt-1 space-y-1">
        <p className="num break-all">
          <span className="text-fg/70">{t('describe.raw_name')}</span> <span translate="no">{name || '—'}</span>
        </p>
        <p className="num break-all" data-testid="outcome-description">
          <span className="text-fg/70">{t('describe.raw_description')}</span> <span translate="no">{description || '—'}</span>
        </p>
      </div>
    </details>
  );
}
